export const IMPORT_POLL_INTERVAL_MS = 2500;
export const IMPORT_POLL_TIMEOUT_MS = 2 * 60 * 1000;

export type MetaImportReport = {
  report_status?:
    | 'completed'
    | 'completed_with_warnings'
    | 'completed_with_errors'
    | 'failed';
  email_body?: string;
  report?: Record<string, unknown>;
};

export type BulkActionProgress = {
  status?: string;
  report?: MetaImportReport | null;
};

export type ImportOutcome =
  | { kind: 'success'; report: MetaImportReport }
  | { kind: 'failed'; report: MetaImportReport | null }
  | { kind: 'email' };

type ImportTracking = {
  progressId: string | null;
  progress: BulkActionProgress | undefined;
  progressUnavailable: boolean;
  startedAt: number;
  now: number;
};

const FAILURE_STATUSES = ['failed', 'cancelled', 'skipped'];

export const deriveImportOutcome = ({
  progressId,
  progress,
  progressUnavailable,
  startedAt,
  now,
}: ImportTracking): ImportOutcome | null => {
  if (progress?.status === 'completed' && progress.report) {
    return progress.report.report_status === 'failed'
      ? { kind: 'failed', report: progress.report }
      : { kind: 'success', report: progress.report };
  }

  if (FAILURE_STATUSES.includes(progress?.status ?? '')) {
    return { kind: 'failed', report: progress?.report ?? null };
  }

  if (
    !progressId ||
    progressUnavailable ||
    now - startedAt >= IMPORT_POLL_TIMEOUT_MS
  ) {
    return { kind: 'email' };
  }

  return null;
};

export const isAwaitingFailureReport = (
  outcome: ImportOutcome | null,
  tracking: ImportTracking
) =>
  outcome?.kind === 'failed' &&
  !outcome.report &&
  !tracking.progressUnavailable &&
  tracking.now - tracking.startedAt < IMPORT_POLL_TIMEOUT_MS;

export const getImportRefetchInterval = (tracking: ImportTracking) => {
  const outcome = deriveImportOutcome(tracking);

  return outcome && !isAwaitingFailureReport(outcome, tracking)
    ? false
    : IMPORT_POLL_INTERVAL_MS;
};

export const isProgressUnavailable = (error: any) =>
  Boolean(error?.isAxiosError) && [403, 404].includes(error.response?.status);

export const shouldRetryImportProgress = (
  error: unknown,
  startedAt: number,
  now: number
) => !isProgressUnavailable(error) && now - startedAt < IMPORT_POLL_TIMEOUT_MS;

export const shouldRefreshObjectCaches = (
  report: MetaImportReport | null | undefined
) => {
  const customObjects = report?.report?.custom_objects as
    | Record<string, unknown>
    | undefined;

  return ['created', 'updated'].some((status) => {
    const items = customObjects?.[status];
    return Array.isArray(items) && items.length > 0;
  });
};

export const getReportSeverity = (
  report: MetaImportReport | null | undefined
) => {
  switch (report?.report_status) {
    case 'completed_with_errors':
      return 'errors';
    case 'completed_with_warnings':
      return 'warnings';
    default:
      return 'ok';
  }
};
